import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import HouseIcon from '@mui/icons-material/House';
import { AppBar, Tabs, Tab, Toolbar, IconButton } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';

// main charts
import Intro from './Intro';
import ExecutiveSummary from './ExecutiveSummary';
import Fees from './Fees';
import Expenses from './Expenses';
import FinancialPerformanceYTD from './FinancialPerformanceYTD';
import YearlyFinancialPerformance from './YearlyFinancialPerformance';
import BalanceSheet from './BalanceSheet';
import CashFlow from './CashFlow';
import Debtors from './Debtors';
import ProjectPerformance from './ProjectPerformance';
import WorkInHand from './WorkInHand';
import Opportunities from './Opportunities';
import WorkGenerated from './WorkGenerated';
import FeeProjection from './FeeProjection';
import ProjectedFinancialPerformance from './ProjectedFinancialPerformance';
import ProjectedCashFlow from './ProjectedCashFlow';
import People from './People';
import KPI from './KPI';

import { chartTypes } from '../../Assets/js/constant';
import { creators as DefaultActions } from '../../Reducers/Reporting/Default';
import { creators as ReportSettingActions } from '../../Reducers/Reporting/ReportSetting';
import DefaultApi from '../../Api/DefaultApi';
import manageApi from '../../Api/ApiManageReportData';
import band from '../../Assets/image/band.png';
import { months } from '../../Utils/Functions';
import appdata from '../../Services/appdata';
import './style.css';

const PageDiv = styled('div')(() => ({
  position: 'absolute',
  overflow: 'auto',
  padding: 8,
  left: 0,
  top: 0,
  width: '100%',
  height: 'calc(100vh - 68px)',
  backgroundColor: 'white',
}));

function Reporting() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const defaultInfo = useSelector((state) => state.Reporting.Default.defaultInfo);
  const reportSetting = useSelector((state) => state.Reporting.ReportSetting.siteSetting);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );
  const isPrintMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('printMode') === '1';
  }, []);

  const [resize, setResize] = useState(false);
  const resizeTimerRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [defaultYear, setDefaultYear] = useState(
    new Date().getMonth() < defaultInfo['FiscalYearStartMonth']
      ? new Date().getFullYear()
      : new Date().getFullYear() + 1
  );
  const [defaultMonth, setDefaultMonth] = useState(
    new Date().getMonth() +
      1 -
      defaultInfo['FiscalYearStartMonth'] >=
      0
      ? new Date().getMonth() -
        defaultInfo['FiscalYearStartMonth'] +
        1
      : new Date().getMonth() +
        defaultInfo['FiscalYearStartMonth'] -
        1
  );
  const [defaultDimDate, setDefaultDimDate] = useState(
    new Date().getMonth() > 9
      ? `${new Date().getFullYear()}${new Date().getMonth()}01`
      : new Date().getMonth() === 0
      ? `${new Date().getFullYear() - 1}1201`
      : `${new Date().getFullYear()}0${new Date().getMonth()}01`,
  );
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(appdata.getItem('company_org_id')
    ? parseInt(appdata.getItem('company_org_id'))
    : 1
  );
  const [tableStructure, setTableStructure] = useState([]);
  const [reportPageStructure, setReportPageStructure] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(false);
  const [printFlag, setPrintFlag] = useState(false);
  
  const onResize = () => {
    if (resizeTimerRef.current) {
      clearTimeout(resizeTimerRef.current);
    }
    resizeTimerRef.current = setTimeout(() => {
      setResize((prev) => !prev);
    }, 150);
  };

  useEffect(() => {
    const asyncFn = async () => {
      setLoading(true);
      if (!isPrintMode) {
        dispatch(DefaultActions.defaultSetCompany(null));
        dispatch(DefaultActions.defaultSetDimDate(null));
      }
      await dispatch(ReportSettingActions.reportSettingGetRequest());
      await dispatch(DefaultActions.defaultGetRequest());

      const path = window.location.pathname;
      let words = path.split('/');
      const type = words[words.length - 1];

      // Sequential API calls to avoid concurrent authentication conflicts
      try {
        const pnlRes = await manageApi.getTemplate('PNL');
        if (pnlRes.status === 200) {
          appdata.setItem('tableStructure', pnlRes.data);
          setTableStructure(pnlRes.data);
          window.addEventListener('resize', onResize);
        } else {
          toast.error(pnlRes.message);
        }
      } catch (error) {
        toast.error(error.message);
      }

      try {
        const companyRes = await DefaultApi.getAllCompanySettings();
        if (companyRes.status === 200) {
          setCompanies(companyRes.data);
        } else {
          toast.error(companyRes.message);
        }
      } catch (error) {
        toast.error(error.message);
      }

      try {
        const pageSelectionRes = await manageApi.getTemplate('Report Page Selection');
        if (pageSelectionRes.status === 200) {
          let pageStructure = pageSelectionRes.data;
          let tabIndex = 0;
          let pageIndex = 0;
          let reportPageStructure = pageStructure.filter(
            (page) => page.active === true,
          );
          reportPageStructure.forEach((page, index) => {
            if (page.title.replace(/\s/g, '') === type) {
              tabIndex = index + 2;
              pageIndex = page.id;
            }
          });
          if (type === 'ExecutiveSummary') {
            tabIndex = 1;
            pageIndex = 1;
          }
          setReportPageStructure(reportPageStructure);
          setTabIndex(tabIndex);
          setPageIndex(pageIndex);
        } else {
          toast.error(pageSelectionRes.message);
        }
      } catch (error) {
        toast.error(error.message);
      }

      if (lastFetchedCompany === selectedCompany && lastFetchedDimDate) {
        setLoading(false);
        setFirstLoad(true);
        setPrintFlag(true);
      } else {
        setTimeout(() => {
          setLoading(false);
          setFirstLoad(true);
          setPrintFlag(true);
        }, 10000);
      }
    };

    asyncFn();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handleChangeTab = (event, tabIndex) => {
    let pageTitle = '';
    let pageIndex = 0;
    if (tabIndex > 1)
      reportPageStructure.forEach((page, index) => {
        if (index === tabIndex - 2) {
          pageTitle = page.title.replace(/\s/g, '');
          pageIndex = page.id;
        }
      });
    else {
      pageTitle = chartTypes[tabIndex];
      pageIndex = tabIndex;
    }

    navigate('/home/reporting/' + pageTitle);
    setTabIndex(tabIndex);
    setPageIndex(pageIndex);
  };

  const defaultHandler = useCallback((event) => {
    if (event.defaultDimDate) {
      setDefaultDimDate(event.defaultDimDate);
    }
    if (event.defaultYear) {
      setDefaultYear(event.defaultYear);
    }
    if (event.defaultMonth) {
      setDefaultMonth(event.defaultMonth);
    }
  }, [dispatch]);

  const handleCompanyChange = async (newValue) => {
    setLoading(true);
    setFirstLoad(false);
    setSelectedCompany(newValue);
    appdata.setItem('company_org_id', newValue);
    
    try {
      const pnlRes = await manageApi.getTemplate('PNL');
      if (pnlRes.status === 200) {
        appdata.setItem('tableStructure', pnlRes.data);
        setTableStructure(pnlRes.data);
      } else {
        toast.error(pnlRes.message);
      }
    } catch (error) {
      toast.error(error.message);
    }

    try {
      const pageSelectionRes = await manageApi.getTemplate('Report Page Selection');
      if (pageSelectionRes.status === 200) {
        const path = window.location.pathname;
        let words = path.split('/');
        const type = words[words.length - 1];

        let pageStructure = pageSelectionRes.data;
        let tabIndex = 0;
        let pageIndex = 0;
        let reportPageStructure = pageStructure.filter(
          (page) => page.active === true,
        );
        reportPageStructure.forEach((page, index) => {
          if (page.title.replace(/\s/g, '') === type) {
            tabIndex = index + 2;
            pageIndex = page.id;
          }
        });
        if (type === 'ExecutiveSummary') {
          tabIndex = 1;
          pageIndex = 1;
        }
        setReportPageStructure(reportPageStructure);
        setTabIndex(tabIndex);
        setPageIndex(pageIndex);
      } else {
        toast.error(pageSelectionRes.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
    
    setLoading(false);
    setFirstLoad(true);
  };

  const pageIds = useMemo(() => {
    return reportPageStructure.map((page) => page.id);
  }, [reportPageStructure]);
  
  const companyName = useMemo(() => {
    if (companies) {
      let idx = companies.findIndex((c) => c.id === selectedCompany);
      if (idx > -1) return companies[idx].CompanyName;
    }
    return '';
  }, [companies, selectedCompany]);

  const default_year = useMemo(() => {
    if (reportSetting.Year) {
      if (
        reportSetting.Month >= defaultInfo['FiscalYearStartMonth']
      ) {
        return reportSetting.Year + 1;
      } else {
        return reportSetting.Year;
      }
    }
    return defaultYear;
  }, [reportSetting, defaultInfo, defaultYear]);

  const default_month = useMemo(() => {
    if (reportSetting.Year) {
      if (
        reportSetting.Month >= defaultInfo['FiscalYearStartMonth']
      ) {
        return reportSetting.Month -
        defaultInfo['FiscalYearStartMonth'] +
        1;
      } else {
        return reportSetting.Month +
        defaultInfo['FiscalYearStartMonth'] -
        1;
      }
    }
    return defaultMonth;
  }, [reportSetting, defaultInfo, defaultMonth]);

  const default_dim_date = useMemo(() => {
    if (reportSetting.Year) {
      return reportSetting.Year.toString() +
      ('0' + reportSetting.Month).slice(-2) +
      '01';
    }
    return defaultDimDate;
  }, [reportSetting, defaultDimDate]);

  const selectedReportPeriod = useMemo(() => {
    const dimDateText = default_dim_date ? String(default_dim_date) : '';
    const dimYear = dimDateText.length >= 4 ? parseInt(dimDateText.slice(0, 4), 10) : null;
    const dimMonth = dimDateText.length >= 6 ? parseInt(dimDateText.slice(4, 6), 10) : null;
    const periodYear = Number(reportSetting.Year) || dimYear || new Date().getFullYear();
    const periodMonth = Number(reportSetting.Month) || dimMonth || Number(default_month) || 1;
    const fiscalStartMonth = Number(defaultInfo['FiscalYearStartMonth']) || 7;
    const computedFiscalYear = periodMonth >= fiscalStartMonth ? periodYear + 1 : periodYear;

    return {
      year: periodYear,
      month: periodMonth,
      fiscalYear: Number(default_year) || computedFiscalYear,
    };
  }, [default_dim_date, reportSetting, default_month, default_year, defaultInfo]);

  const footerPeriodText = useMemo(() => {
    const monthName = months[selectedReportPeriod.month - 1] || 'Current Period';
    return `${monthName} ${selectedReportPeriod.year}`;
  }, [selectedReportPeriod]);

  const renderReportingFooter = (pageTitle) => (
    <div className="reporting-footer-content">
      <img className="band-image" src={band} alt="band" />
      <div className="bottom-mark">
        <span>
          {companyName} | {footerPeriodText} | {pageTitle}
        </span>
      </div>
    </div>
  );

  useEffect(() => {
    if (firstLoad) {
      dispatch(DefaultActions.defaultSetCompany(selectedCompany));
      dispatch(DefaultActions.defaultSetDimDate(default_dim_date));
    }
  }, [dispatch, firstLoad, selectedCompany, default_dim_date]);
  
  const handleExportPDF = async () => {
    // The main application opens a stable-width print window so visx charts
    // are laid out before capture. A print window can export at any size.
    if (!isPrintMode && (window.innerWidth > 1310 || window.innerWidth < 1230)) {
      const url = `${window.location.origin}/home/reporting/ExecutiveSummary?printMode=1`;
      const popup = window.open(
        url,
        `Management_Reporting_${Date.now()}`,
        'width=1270,height=970,resizable=yes,scrollbars=yes',
      );
      if (!popup) toast.warning('Please allow pop-ups to open the PDF print view.');
      return;
    }

    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      await (document.fonts?.ready || Promise.resolve());

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4', compress: true });
      const paperWidth = pdf.internal.pageSize.getWidth();
      const paperHeight = pdf.internal.pageSize.getHeight();
      const headerHeight = 27;
      const footerHeight = 27;
      const horizontalMargin = 10;
      const contentTop = headerHeight + 8;
      const contentHeight = paperHeight - headerHeight - footerHeight - 16;
      const contentWidth = paperWidth - 2 * horizontalMargin;
      const pageWidthPx = document.getElementById('page1')?.clientWidth || window.innerWidth;
      const sourceToPdfScale = contentWidth / pageWidthPx;
      const maxSliceHeight = Math.floor(contentHeight / sourceToPdfScale);
      const preferredRenderScale = Math.min(1.5, Math.max(1.25, window.devicePixelRatio || 1));
      const pages = [
        { id: 0, title: 'Cover' },
        { id: 1, title: 'Executive Summary' },
        ...reportPageStructure.filter((page) => page.active).map((page) => ({
          id: page.id,
          title: page.title,
        })),
      ];
      const hiddenIds = [
        'controllLegend', 'executive_summary_selector',
        'cashflow_year_selector', 'fpy_year_selector', 'yfp_year_selector',
        'expense_year_selector', 'debtors_year_selector', 'pp_year_selector',
        'opp_year_selector', 'wg_year_selector', 'pcf_year_selector',
        'fpy_percent', 'pfp_year_selector', 'pfp_percent', 'people_year_selector',
        'fees_year_selector', 'fp_year_selector',
      ];
      let outputPageNo = 0;

      const newSheet = (title, continued = false) => {
        if (outputPageNo > 0) pdf.addPage();
        outputPageNo += 1;
        pdf.setFillColor(0, 0, 0);
        pdf.rect(0, 0, paperWidth, headerHeight, 'F');
        pdf.rect(0, paperHeight - footerHeight, paperWidth, footerHeight, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(255, 255, 255);
        pdf.text(`${title}${continued ? ' (continued)' : ''} - FY${selectedReportPeriod.fiscalYear}`, 11, 18);
        pdf.setFontSize(9);
        pdf.text(`${companyName} | ${footerPeriodText} | ${outputPageNo}`,
          paperWidth - 10, paperHeight - 10, { align: 'right' });
        pdf.setTextColor(0, 0, 0);
      };

      // Reflow the REPORT IN html2canvas's PRIVATE DOCUMENT CLONE, never the
      // visible report. Scrollable charts must expand IN PLACE so that By
      // Director, By Project and all subsequent sections keep their order.
      // In V18-20 they were appended afterwards, which produced blank pages,
      // detached axes, missing rows and charts out of sequence.
      const preparePrintClone = (doc, pageId, measurement) => {
        const root = doc.getElementById(`page${pageId}`);
        if (!root) return;
        root.querySelectorAll('.reporting-tooltip, .reporting-footer-content').forEach((element) => {
          element.style.display = 'none';
        });
        hiddenIds.forEach((id) => {
          const element = root.querySelector(`[id="${id}"]`);
          if (element) element.style.display = 'none';
        });
        root.style.position = 'relative';
        root.style.left = '0';
        root.style.top = '0';
        root.style.width = `${pageWidthPx}px`;
        root.style.height = 'auto';
        root.style.minHeight = '0';
        root.style.maxHeight = 'none';
        root.style.overflow = 'visible';
        root.style.zIndex = '1';
        root.scrollTop = 0;

        // Pages use a fixed 89vh wrapper for the interactive UI. That height
        // must not dictate the size or the pagination of a printed report.
        const wrapper = root.firstElementChild;
        if (wrapper) {
          wrapper.style.height = 'auto';
          wrapper.style.maxHeight = 'none';
          wrapper.style.minHeight = '0';
          wrapper.style.overflowY = 'visible';
        }

        // First identify the independently scrolling charts while their
        // original dimensions are intact. Expanding them later would otherwise
        // remove the scrollHeight > clientHeight signal.
        const scrollers = Array.from(root.querySelectorAll('*')).filter((element) => {
          const style = doc.defaultView.getComputedStyle(element);
          return /auto|scroll/.test(style.overflowY) &&
            element.clientHeight > 15 && element.scrollHeight > element.clientHeight + 2;
        });
        scrollers.forEach((element) => {
          element.style.height = `${element.scrollHeight}px`;
          element.style.maxHeight = 'none';
          element.style.minHeight = '0';
          element.style.overflow = 'visible';
          element.scrollTop = 0;
          // A fixed-height ancestor (notably the fee director chart's 229px
          // outer panel) would otherwise overlap the following project chart.
          for (let ancestor = element.parentElement; ancestor && ancestor !== root; ancestor = ancestor.parentElement) {
            if (ancestor.style.height || ancestor.style.maxHeight) {
              ancestor.style.height = 'auto';
              ancestor.style.maxHeight = 'none';
            }
          }
        });

        if (measurement) {
          const bounds = root.getBoundingClientRect();
          measurement.height = Math.max(1, Math.ceil(root.scrollHeight));
          measurement.breakpoints = [];
          // Prefer printing between rows/paragraphs rather than through text
          // or halfway through a bar. The overall content is never reordered.
          root.querySelectorAll('p, tr, .subtitle, h1, h2, h3, div').forEach((element) => {
            if (element.style.display === 'none') return;
            const rect = element.getBoundingClientRect();
            if (!rect.height || !rect.width) return;
            const rowLike = rect.height >= 17 && rect.height <= 36 && rect.width >= 80;
            const heading = element.matches('.subtitle, h1, h2, h3');
            if (!rowLike && !heading) return;
            const y = Math.round(rect.top - bounds.top);
            if (y > 0 && y < measurement.height) measurement.breakpoints.push(y);
          });
          measurement.breakpoints = Array.from(new Set(measurement.breakpoints)).sort((a, b) => a - b);
        }
      };

      const captureOptions = {
        backgroundColor: '#ffffff',
        scale: preferredRenderScale,
        useCORS: true,
        logging: false,
        imageTimeout: 15000,
        scrollX: 0,
        scrollY: 0,
        windowWidth: pageWidthPx,
        windowHeight: window.innerHeight,
      };

      const planSlices = (height, breakpoints) => {
        const slices = [];
        let start = 0;
        while (start < height) {
          let end = Math.min(height, start + maxSliceHeight);
          if (end < height) {
            // Use the nearest row boundary within the final 18% of this page.
            const minBreak = start + Math.floor(maxSliceHeight * 0.82);
            for (let i = breakpoints.length - 1; i >= 0; i -= 1) {
              const candidate = breakpoints[i];
              if (candidate > end) continue;
              if (candidate >= minBreak) end = candidate;
              break;
            }
          }
          if (end <= start) end = Math.min(height, start + maxSliceHeight);
          slices.push([start, end]);
          start = end;
        }
        return slices;
      };

      // html2canvas captures pixels, not PDF-native text. Retain a single
      // consistent width/height scale when drawing each slice to avoid the
      // stretched and vertically compressed charts in the V18 export.
      const appendCanvas = (canvas, heightPx) => {
        const imageHeight = heightPx * sourceToPdfScale;
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', horizontalMargin,
          contentTop, contentWidth, imageHeight, undefined, 'FAST');
        canvas.width = 0;
        canvas.height = 0;
      };

      for (const page of pages) {
        const root = document.getElementById(`page${page.id}`);
        if (!root) continue;
        const measurement = { height: 1, breakpoints: [] };
        const probe = await html2canvas(root, {
          ...captureOptions,
          scale: 1,
          width: 1,
          height: 1,
          onclone: (doc) => preparePrintClone(doc, page.id, measurement),
        });
        probe.width = 0;
        probe.height = 0;
        const height = measurement.height;
        const slices = planSlices(height, measurement.breakpoints);

        // Small/normal reports are rasterised ONCE, then sliced from the same
        // canvas. This guarantees continuous coordinates with no repeats or
        // dropped sections at page boundaries.
        // Reduce resolution slightly (rather than producing a giant canvas)
        // on exceptionally long reports. This avoids the per-slice html2canvas
        // offset bug for ordinary clients while keeping print resolution high.
        const pageRenderScale = Math.min(
          preferredRenderScale,
          Math.sqrt(22000000 / (pageWidthPx * height)),
          28000 / height,
        );
        const captureAllAtOnce = pageRenderScale >= 0.85;
        let fullCanvas = null;
        if (captureAllAtOnce) {
          fullCanvas = await html2canvas(root, {
            ...captureOptions,
            scale: pageRenderScale,
            width: pageWidthPx,
            height,
            onclone: (doc) => preparePrintClone(doc, page.id),
          });
        }

        for (const [sliceIndex, [start, end]] of slices.entries()) {
          let canvas;
          if (fullCanvas) {
            const startPx = Math.round(start * pageRenderScale);
            const endPx = Math.min(fullCanvas.height, Math.round(end * pageRenderScale));
            canvas = document.createElement('canvas');
            canvas.width = fullCanvas.width;
            canvas.height = Math.max(1, endPx - startPx);
            canvas.getContext('2d').drawImage(fullCanvas,
              0, startPx, fullCanvas.width, canvas.height,
              0, 0, canvas.width, canvas.height);
          } else {
            // Avoid exceeding browser canvas limits for very large clients.
            canvas = await html2canvas(root, {
              ...captureOptions,
              scale: preferredRenderScale,
              x: 0,
              y: start,
              width: pageWidthPx,
              height: end - start,
              onclone: (doc) => preparePrintClone(doc, page.id),
            });
          }
          newSheet(page.title, sliceIndex > 0);
          appendCanvas(canvas, end - start);
        }
        if (fullCanvas) {
          fullCanvas.width = 0;
          fullCanvas.height = 0;
        }
      }

      if (outputPageNo === 0) throw new Error('No report pages available for PDF export.');
      pdf.save('ManagementReporting.pdf');
    } catch (error) {
      console.error('R4D PDF export failed:', error);
      toast.error(`PDF generation error: ${error.message || 'Please try again.'}`);
    } finally {
      setExporting(false);
    }
  };

  const onSignout = () => {
    appdata.localClear();
    appdata.clear();
  };

  const onGoHome = () => {
    appdata.localClear();
  };
  
  return (
    <div className={isPrintMode ? "reporting-page reporting-print-mode" : "reporting-page"}>
      <div id="content-wrap">
        {(loading || exporting) && (
          <CircularProgress
            size={68}
            sx={{
              color: '#4caf50',
              position: 'absolute',
              top: '45%',
              left: '50%',
              zIndex: 9999,
            }}
          />
        )}

        <AppBar position="relative" color="default">
          <Toolbar>
            <IconButton component="a" href="/home/main" onClick={onGoHome}>
              <HouseIcon />
            </IconButton>

            <div style={{ width: 'calc(100% - 80px)' }}>
              <Tabs
                value={tabIndex}
                onChange={handleChangeTab}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  style: { backgroundColor: 'transparent' },
                }}
                inkbarstyle={{ color: 'black', fontWeight: 'bold' }}
                sx={{
                  '& .MuiTabScrollButton-root': {
                    width: 65,
                  },
                }}
              >
                <Tab label="Intro" className="ft-bold" />
                <Tab label="Executive Summary" className="ft-bold" />
                {reportPageStructure.map((page) => (
                  <Tab key={page.id} label={page.title} className="ft-bold" />
                ))}
              </Tabs>
            </div>

            <IconButton onClick={onSignout} className="sign-out-but">
              <ExitToAppIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <div id="myMm" style={{ height: '1mm' }} />

        <div style={{ position: 'relative' }} id="pdf-content">
          <PageDiv
            style={{ zIndex: pageIndex === 0 ? 999 : 1 }}
            id="page0"
          >
            <Intro
              loading={loading}
              companies={companies}
              selectedCompany={selectedCompany}
              handleCompanyChange={handleCompanyChange}
            />
            {renderReportingFooter('Intro')}
          </PageDiv>

          {tableStructure.length > 0 ? (
            <>
              <PageDiv
                style={{ zIndex: pageIndex === 1 ? 999 : 1 }}
                id="page1"
              >
                <ExecutiveSummary                  
                  defaultYear={default_year}                  
                  defaultMonth={default_month}                  
                  defaultDimDate={default_dim_date}
                  defaultStartMonth={
                    defaultInfo['FiscalYearStartMonth']
                      ? defaultInfo['FiscalYearStartMonth']
                      : default_month
                  }
                  selectedCompany={selectedCompany}                  
                  exporting={exporting}
                  printFlag={printFlag}
                  reportPageStructure={reportPageStructure}
                  defaultHandler={defaultHandler}
                  handleExportPDF={handleExportPDF}
                />
                {renderReportingFooter('Executive Summary')}
              </PageDiv>
              {pageIds.indexOf(2) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 2 ? 999 : 1 }}
                  id="page2"
                >
                  <Fees                    
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    feesSetting={reportSetting.Fees}
                    bubbleFlag={reportPageStructure.find(item => item.id === 2).bubble}
                  />
                  {renderReportingFooter('Fees')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(3) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 3 ? 999 : 1 }}
                  id="page3"
                >
                  <Expenses                    
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    expensesSetting={reportSetting.Expenses}
                    bubbleFlag={reportPageStructure.find(item => item.id === 3).bubble}
                  />
                  {renderReportingFooter('Expenses')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(4) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 4 ? 999 : 1 }}
                  id="page4"
                >
                  <FinancialPerformanceYTD
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    tableStructure={tableStructure}
                    financialPerformanceYTDSetting={
                      reportSetting.FinancialPerformanceYTD
                    }
                    profitSetting={reportSetting.ProfitTitle}
                    tabIndex={tabIndex}
                    bubbleFlag={reportPageStructure.find(item => item.id === 4).bubble}
                  />
                  {renderReportingFooter('Financial Performance Ytd')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(5) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 5 ? 999 : 1 }}
                  id="page5"
                >
                  <YearlyFinancialPerformance                    
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    tableStructure={tableStructure}
                    yearlyFinancialPerformanceSetting={
                      reportSetting.YearlyFinancialPerformance
                    }
                    profitSetting={reportSetting.ProfitTitle}
                    tabIndex={tabIndex}
                    bubbleFlag={reportPageStructure.find(item => item.id === 5).bubble}
                  />
                  {renderReportingFooter('Yearly Financial Performance')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(6) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 6 ? 999 : 1 }}
                  id="page6"
                >
                  <BalanceSheet
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}                    
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    bubbleFlag={reportPageStructure.find(item => item.id === 6).bubble}
                  />
                  {renderReportingFooter('Balance Sheet')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(7) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 7 ? 999 : 1 }}
                  id="page7"
                >
                  <CashFlow
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}                    
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    bubbleFlag={reportPageStructure.find(item => item.id === 7).bubble}
                  />
                  {renderReportingFooter('Cash Flow')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(8) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 8 ? 999 : 1 }}
                  id="page8"
                >
                  <Debtors
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}                    
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    debtorsSetting={reportSetting.Debtors}
                    bubbleFlag={reportPageStructure.find(item => item.id === 8).bubble}
                  />
                  {renderReportingFooter('Debtors')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(9) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 9 ? 999 : 1 }}
                  id="page9"
                >
                  <ProjectPerformance
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}                    
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    projectPerformanceSetting={
                      reportSetting.ProjectPerformance
                    }
                    bubbleFlag={reportPageStructure.find(item => item.id === 9).bubble}
                  />
                  {renderReportingFooter('Project Performance')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(10) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 10 ? 999 : 1 }}
                  id="page10"
                >
                  <WorkInHand                    
                    defaultYear={default_year}
                    defaultDimDate={default_dim_date}
                    selectedCompany={selectedCompany}
                    workInHandSetting={reportSetting.WorkInHand}
                    bubbleFlag={reportPageStructure.find(item => item.id === 10).bubble}
                  />
                  {renderReportingFooter('Work In Hand')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(11) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 11 ? 999 : 1 }}
                  id="page11"
                >
                  <Opportunities
                    defaultYear={default_year}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    selectedCompany={selectedCompany}
                    opportunitiesSetting={reportSetting.Opportunities}
                    bubbleFlag={reportPageStructure.find(item => item.id === 11).bubble}
                  />
                  {renderReportingFooter('Opportunities')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(12) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 12 ? 999 : 1 }}
                  id="page12"
                >
                  <WorkGenerated
                    defaultYear={default_year}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    selectedCompany={selectedCompany}
                    workGeneratedSetting={reportSetting.WorkGenerated}
                    bubbleFlag={reportPageStructure.find(item => item.id === 12).bubble}
                  />
                  {renderReportingFooter('Work Generated')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(13) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 13 ? 999 : 1 }}
                  id="page13"
                >
                  <FeeProjection
                    defaultYear={default_year}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    selectedCompany={selectedCompany}
                    feeProjectionSetting={reportSetting.FeeProjection}
                    bubbleFlag={reportPageStructure.find(item => item.id === 13).bubble}
                  />
                  {renderReportingFooter('Fee Projection')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(14) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 14 ? 999 : 1 }}
                  id="page14"
                >
                  <ProjectedFinancialPerformance
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}                    
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    tableStructure={tableStructure}
                    projectedFinancialPerformanceSetting={
                      reportSetting.ProjectedFinancialPerformance
                    }
                    profitSetting={reportSetting.ProfitTitle}
                    tabIndex={tabIndex}
                    bubbleFlag={reportPageStructure.find(item => item.id === 14).bubble}
                  />
                  {renderReportingFooter('Projected Financial Performance')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(15) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 15 ? 999 : 1 }}
                  id="page15"
                >
                  <ProjectedCashFlow
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}                    
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    bubbleFlag={reportPageStructure.find(item => item.id === 15).bubble}
                  />
                  {renderReportingFooter('Projected Cash Flow')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(16) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 16 ? 999 : 1 }}
                  id="page16"
                >
                  <People
                    defaultYear={default_year}
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    selectedCompany={selectedCompany}
                    bubbleFlag={reportPageStructure.find(item => item.id === 16).bubble}
                  />
                  {renderReportingFooter('People')}
                </PageDiv>
              ) : null}
              {pageIds.indexOf(17) !== -1 ? (
                <PageDiv
                  style={{ zIndex: pageIndex === 17 ? 999 : 1 }}
                  id="page17"
                >
                  <KPI
                    defaultYear={default_year}
                    defaultStartMonth={
                      defaultInfo['FiscalYearStartMonth']
                        ? defaultInfo['FiscalYearStartMonth']
                        : default_month
                    }
                    defaultMonth={default_month}
                    defaultDimDate={default_dim_date}
                    selectedCompany={selectedCompany}
                    kpiSetting={reportSetting.Kpi}
                    bubbleFlag={reportPageStructure.find(item => item.id === 17).bubble}
                  />
                  {renderReportingFooter('Business Performance')}
                </PageDiv>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Reporting;
